import { useState } from "react";

import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Pagination,
  Popover,
  Row,
  Select,
  Spin,
  Table,
  Tag,
  TimePicker,
} from "antd";

import type { TableColumnsType } from "antd";

import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  getCampaigns,
} from "../../../services/commonController";
import dayjs from "dayjs";
import CreateCampaignForm from "./CreateCampaignTable";
import axios from "axios";
import { getDialStatusCount, UpdateCampaignPayload } from "../../../services/adminController";
import { updatePredictiveCampaign } from "../../../services/adminController";

interface Campaign {
  id: number;
  name: string;
  campaign_type: string;
  phonebook_id: string;
  trunk: string;
  status: string;
  dialing_ratio: number;
  retry_attempt: number;
  retry_interval_sec: number;
  created_at: string;
  updated_at: string;
  active_time_start: string | null;
  active_time_end: string | null;
}


interface DialStatusCountProps {
  campaignId: number;
}

export const CREATE_CAMPAIGN = "/dialer/campaigns";

export interface CreateCampaignPayload {
  campaigns: {
    active_time_end: string;
    active_time_start: string;
    campaign_type: string;
    dialing_ratio: string;
    name: string;
    phonebook_id: string;
    retry_attempt: number;
    retry_interval_sec: number;
    status: string;
    trunk: string;
  }[];
}

export async function createCampaign(
  payload: CreateCampaignPayload
): Promise<any> {
  const res = await axios.post(
    CREATE_CAMPAIGN,
    payload
  );

  if (res.status >= 200 && res.status < 300) {
    return res.data;
  }

  throw new Error("Failed to create campaign");
}

 

const CampaignReport = () => {
  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const [currentPage, setCurrentPage] =
    useState(1);

    const [editingCampaign, setEditingCampaign] =
  useState<Campaign | null>(null);

const [isEditModalOpen, setIsEditModalOpen] =
  useState(false);
const [editingPhonebookId, setEditingPhonebookId] =
  useState<string | null>(null);
  const [form] = Form.useForm();

  const queryClient =
    useQueryClient();
const [
  hoveredCampaignId,
  setHoveredCampaignId,
] = useState<number | null>(null);
  const [messageApi, contextHolder] =
    message.useMessage();

const {
  data: dialStatusResponse,
  isLoading: isDialStatusLoading,
  isError: isDialStatusError,
} = useQuery({
  queryKey: [
    "dialStatusCount",
    hoveredCampaignId,
  ],

  queryFn: () =>
    getDialStatusCount(
      hoveredCampaignId as number
    ),

  enabled:
    hoveredCampaignId !== null,

  staleTime: 30 * 1000,
});

const {
  mutate: updateCampaignMutation,
  isPending: isUpdatingCampaign,
} = useMutation({
  mutationFn: ({
    campaignId,
    payload,
  }: {
    campaignId: number;
    payload: UpdateCampaignPayload;
  }) => updatePredictiveCampaign(campaignId, payload),

  onSuccess: () => {
    messageApi.success("Campaign updated successfully");

    setIsEditModalOpen(false);
    setEditingCampaign(null);

    form.resetFields();

    queryClient.invalidateQueries({
      queryKey: ["campaigns"],
    });
  },

  onError: (error: any) => {
    messageApi.error(
      error?.response?.data?.message ||
        "Failed to update campaign"
    );
  },
});


const campaignTableColumns: TableColumnsType<Campaign> = [


{
  title: "CAMPAIGN NAME",
  dataIndex: "name",
  key: "name",
  width: 180,

  render: (name, record) => (
    <Popover
      trigger="hover"
      placement="rightTop"
      mouseEnterDelay={0.2}
      mouseLeaveDelay={0.1}
      content={
        <div style={{ minWidth: 280 }}>
          {isDialStatusLoading ? (
            <div className="flex justify-center p-3">
              <Spin size="small" />
            </div>
          ) : isDialStatusError ? (
            <div className="text-red-500 p-2">
              Failed to fetch dial status
            </div>
          ) : dialStatusResponse ? (
            <>
              <div className="font-semibold text-base mb-3">
                Dial Status
              </div>

              <div className="flex justify-between mb-2">
                <span>Campaign ID</span>
                <strong>
                  {dialStatusResponse.data
                    ?.campaignId ?? "-"}
                </strong>
              </div>

              <div className="flex justify-between mb-2">
                <span>Phonebook ID</span>
                <strong>
                  {dialStatusResponse.data
                    ?.phonebookId ?? "-"}
                </strong>
              </div>

              <div className="flex justify-between mb-2">
                <span>Total Cases</span>
                <strong>
                  {dialStatusResponse.data
                    ?.total ?? 0}
                </strong>
              </div>

              <div className="flex justify-between mb-2">
                <span>Answered</span>
                <Tag color="green">
                  {dialStatusResponse.data
                    ?.ANSWERED ?? 0}
                </Tag>
              </div>

              <div className="flex justify-between mb-2">
                <span>Pending Cases</span>
                <Tag color="blue">
                  {dialStatusResponse.data
                    ?.NEW ?? 0}
                </Tag>
              </div>

              <div className="flex justify-between">
                <span>No Answer</span>
                <Tag color="red">
                  {dialStatusResponse.data
                    ?.NO_ANSWER ?? 0}
                </Tag>
              </div>
            </>
          ) : (
            <span>
              Hover to load dial status
            </span>
          )}
        </div>
      }
      onOpenChange={(open) => {
        if (open) {
          setHoveredCampaignId(
            record.id
          );
        } else {
          setHoveredCampaignId(null);
        }
      }}
    >
      <span
        style={{
          cursor: "pointer",
          fontWeight: 500,
        }}
      >
        {name}
      </span>
    </Popover>
  ),
},

  {
    title: "PHONEBOOK ID",
    dataIndex: "phonebook_id",
    key: "phonebook_id",
    width: 110,
  },

  {
    title: "TYPE",
    dataIndex: "campaign_type",
    key: "campaign_type",
    width: 100,
  },

  {
    title: "STATUS",
    dataIndex: "status",
    key: "status",
    width: 80,

    render: (status) => (
      <Tag
        color={
          status === "active"
            ? "green"
            : "red"
        }
        className="m-0"
      >
        {status?.toUpperCase() || "-"}
      </Tag>
    ),
  },

  {
    title: "ACTIVE TIME",
    key: "active_time",
    width: 130,

    render: (_, record) =>
      record.active_time_start &&
      record.active_time_end
        ? `${record.active_time_start} - ${record.active_time_end}`
        : "NA",
  },
  {
  title: "ACTION",
  key: "action",
  width: 100,

  render: (_, record) => (
    <Button
      type="link"
      size="small"
      onClick={() => {
        setEditingCampaign(record);
  setEditingPhonebookId(
    String(record.phonebook_id)
  );
        form.setFieldsValue({
          name: record.name,
        active_time_start: record.active_time_start
  ? dayjs(record.active_time_start, "HH:mm:ss")
  : null,

active_time_end: record.active_time_end
  ? dayjs(record.active_time_end, "HH:mm:ss")
  : null,

          dialing_ratio: record.dialing_ratio,
            phonebook_id: String(
    editingCampaign?.phonebook_id),
          retry_attempt: record.retry_attempt,
          retry_interval_sec:
            record.retry_interval_sec,

          status: record.status,
        });

        setIsEditModalOpen(true);
      }}
    >
      Edit
    </Button>
  ),
},
];

  const {
    data: campaignsResponse,
    isPending,
    isRefetching,
  } = useQuery({
    queryKey: [
      "campaigns",
      currentPage,
    ],

    queryFn: () =>
      getCampaigns(
        currentPage - 1,
        1000
      ),

    placeholderData:
      keepPreviousData,

    refetchOnMount: true,
  });




  const campaigns: Campaign[] =
    campaignsResponse?.data?.campaigns ??
    [];

  // =====================================================
  // CREATE CAMPAIGN MUTATION
  // =====================================================

  const {
    mutate: createCampaignMutation,
    isPending: isCreatingCampaign,
  } = useMutation({
    mutationFn: createCampaign,

    onSuccess: () => {
      messageApi.success(
        "Campaign created successfully"
      );

      form.resetFields();

      setIsModalOpen(false);

      // Refresh campaign list
      queryClient.invalidateQueries({
        queryKey: ["campaigns"],
      });
    },

    onError: (error: any) => {
      console.error(
        "Create campaign error:",
        error
      );

      messageApi.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to create campaign"
      );
    },
  });

  // =====================================================
  // OPEN MODAL
  // =====================================================

  const handleOpenModal = () => {
    form.resetFields();

    setIsModalOpen(true);
  };

  // =====================================================
  // CLOSE MODAL
  // =====================================================

  const handleCloseModal = () => {
    form.resetFields();

    setIsModalOpen(false);
  };

  // =====================================================
  // CREATE CAMPAIGN
  // =====================================================

  const handleCreateCampaign =
    async () => {
      try {
        // Validate form
        const values =
          await form.validateFields();

   

        // =================================================
        // BUILD API PAYLOAD
        // =================================================

   
      const payload: CreateCampaignPayload = {
  campaigns: [
    {
      name: values.name,

      active_time_start:
        values.active_time_start?.format("hh:mm"),

      active_time_end:
        values.active_time_end?.format("hh:mm"),

      dialing_ratio: String(
        values.dialing_ratio
      ),

      phonebook_id: String(
        values.phonebook_id
      ),

      retry_attempt: Number(
        values.retry_attempt
      ),

      retry_interval_sec: Number(
        values.retry_interval_sec
      ),

      trunk: "ttl-endpoint",
      status: "active",
      campaign_type: "predictive",
    },
  ],
};

     

        // =================================================
        // POST API
        // =================================================

        createCampaignMutation(
          payload
        );
      } catch (error) {
        console.error(
          "Form validation failed:",
          error
        );
      }
    };

  return (
    <>
      {contextHolder}

      <Col>
        {/* =================================================
            HEADER
            ================================================= */}

        <Row className="flex justify-end items-center mb-5">
          <Button
            type="primary"
            onClick={handleOpenModal}
          >
            Create Campaign
          </Button>
        </Row>

        {/* =================================================
            CAMPAIGN TABLE
            ================================================= */}

        <Table
          loading={
            isPending ||
            isRefetching
          }
          columns={
            campaignTableColumns
          }
          dataSource={campaigns}
          rowKey={(record) =>
            record.id
          }
          pagination={false}
          virtual
        />

        {/* =================================================
            PAGINATION
            ================================================= */}

        <Row className="flex justify-center pt-6 pb-6">
          <Pagination
            current={currentPage}
            total={
              campaignsResponse?.data
                ?.totalResults ?? 0
            }
            pageSize={
              campaignsResponse?.data
                ?.pageSize ?? 10
            }
            pageSizeOptions={[
              "10",
              "20",
              "50",
              "100",
            ]}
            onChange={(page) => {
              setCurrentPage(page);
            }}
          />
        </Row>
      </Col>

      {/* =====================================================
          CREATE CAMPAIGN MODAL
          ===================================================== */}

      <Modal
        title="Create Campaign"
        open={isModalOpen}
        onCancel={
          handleCloseModal
        }
        onOk={
          handleCreateCampaign
        }
        okText="Create Campaign"
        cancelText="Cancel"
        centered
        confirmLoading={
          isCreatingCampaign
        }
        // destroyOnHidden
      >
        <CreateCampaignForm
          form={form}
        />
      </Modal>
      <Modal
  title="Edit Campaign"
  open={isEditModalOpen}
  onCancel={() => {
    setIsEditModalOpen(false);
    setEditingCampaign(null);
    form.resetFields();
  }}
  onOk={() => {
    form.submit();
  }}
  confirmLoading={isUpdatingCampaign}
  okText="Update Campaign"
  destroyOnClose
>
<Form
  form={form}
  layout="vertical"
  onFinish={(values) => {
  if (!editingCampaign) return;

  const payload: UpdateCampaignPayload = {
    campaigns: [
      {
        // Editable fields
        active_time_start:
          values.active_time_start?.format("HH:mm:ss"),

        active_time_end:
          values.active_time_end?.format("HH:mm:ss"),

        dialing_ratio:
          String(values.dialing_ratio),

        status: values.status,

        // Auto-populated from existing campaign
        campaign_type:
          editingCampaign.campaign_type,

        name:
          editingCampaign.name,

        phonebook_id:
          String(editingCampaign.phonebook_id),

        retry_attempt:
          Number(editingCampaign.retry_attempt),

        retry_interval_sec:
          Number(editingCampaign.retry_interval_sec),

        trunk:
          editingCampaign.trunk,
      },
    ],
  };



  updateCampaignMutation({
    campaignId: editingCampaign.id,
    payload,
  });
  }}
>
  <Form.Item
    name="active_time_start"
    label="Active Time Start"
    rules={[
      {
        required: true,
        message: "Please select start time",
      },
    ]}
  >
    <TimePicker
      use12Hours
      format="h:mm A"
      className="w-full"
    />
  </Form.Item>

  <Form.Item
    name="active_time_end"
    label="Active Time End"
    rules={[
      {
        required: true,
        message: "Please select end time",
      },
    ]}
  >
    <TimePicker
      use12Hours
      format="h:mm A"
      className="w-full"
    />
  </Form.Item>

  <Form.Item
    name="dialing_ratio"
    label="Dialing Ratio"
    rules={[
      {
        required: true,
        message: "Please enter dialing ratio",
      },
    ]}
  >
    <Input placeholder="Dialing ratio" />
  </Form.Item>

  <Form.Item
    name="status"
    label="Status"
    rules={[
      {
        required: true,
        message: "Please select status",
      },
    ]}
  >
    <Select
      options={[
        {
          label: "Active",
          value: "active",
        },
        {
          label: "Inactive",
          value: "inactive",
        },
      ]}
    />
  </Form.Item>
</Form>
</Modal>
    </>
  );
};

export default CampaignReport;